// packages/core/src/quizzes/content/schoolMatch.ts
// ─────────────────────────────────────────────────────────────────────────
// "Which School of Thought Are You?" (tradeoff engine)
//
// Eight questions routing to eight representative Schools of Thought
// (out of the 39 in Nisk). Balanced matrix: every school appears in
// exactly three questions at weight 2 and nowhere else, so a consistent
// picker scores 6 on their school and at most 4 anywhere else. Ties mean
// the person is genuinely split; the runner shows the runner-up.
//
// Matrix (question → the three schools its options map to):
//   1 boglehead / cashflow / fire      2 behavioral / cashflow / boglehead
//   3 frugality / income / rich-life   4 fire / coast / rich-life
//   5 rich-life / frugality / income   6 behavioral / income / coast
//   7 boglehead / cashflow / coast     8 fire / frugality / behavioral
//
// Per-axis routes go to the school page. For anonymous visitors that
// route lands on the login wall with the destination stashed, so the
// matched school becomes the first thing they see after signing up.
// School ids verified against SchoolMeta: boglehead-indexing,
// fire-movement, coast-fi-slow-fi, rich-life-ramit, cashflow-assets,
// behavioral-finance, frugality, income-expansion.
// ─────────────────────────────────────────────────────────────────────────

import type { TradeoffQuizContent } from '../DiscoveryTypes';

const school = (id: string): string => `/learn/schools/${id}`;

export const SCHOOL_MATCH: TradeoffQuizContent = {
	meta: {
		id: 'school-match',
		title: 'Which School of Thought Are You?',
		hook: '39 different Schools of Thought. Which one do you belong to?',
		engine: 'tradeoff',
		minutes: 3,
		status: 'live',
	},

	questions: [
		{
			type: 'tradeoff',
			id: 'sm-dream-state',
			prompt: 'Your investing dream state:',
			options: [
				{
					label: 'Automatic and boring. I never think about it.',
					weights: { 'boglehead-indexing': 2 },
				},
				{
					label: 'Income hitting the account every month, rain or shine.',
					weights: { 'cashflow-assets': 2 },
				},
				{
					label: 'A finish line I can sprint toward.',
					weights: { 'fire-movement': 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sm-crash',
			prompt: 'A market crash hits the headlines. Your honest first thought:',
			options: [
				{
					label: 'What is my own brain about to do to me?',
					weights: { 'behavioral-finance': 2 },
				},
				{
					label: 'My income streams still pay either way.',
					weights: { 'cashflow-assets': 2 },
				},
				{
					label: 'Nothing. The plan already assumed this.',
					weights: { 'boglehead-indexing': 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sm-win',
			prompt: 'The money win that would feel best this year:',
			options: [
				{
					label: 'Cutting $500 a month without feeling it.',
					weights: { frugality: 2 },
				},
				{
					label: 'A raise or side income worth $1,000 a month.',
					weights: { 'income-expansion': 2 },
				},
				{
					label: 'Spending twice as much on the one thing I love, guilt free.',
					weights: { 'rich-life-ramit': 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sm-retirement',
			prompt: 'Retirement, honestly:',
			options: [
				{
					label: 'As early as the math allows. Every year counts.',
					weights: { 'fire-movement': 2 },
				},
				{
					label: 'Never fully. Lighter work on a longer runway.',
					weights: { 'coast-fi-slow-fi': 2 },
				},
				{
					label: 'On time, having lived well the whole way there.',
					weights: { 'rich-life-ramit': 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sm-sentence',
			prompt: 'Which sentence sounds most like you?',
			options: [
				{
					label: 'Cut ruthlessly on what you do not care about to spend big on what you do.',
					weights: { 'rich-life-ramit': 2 },
				},
				{
					label: 'A dollar not spent is a dollar earned twice.',
					weights: { frugality: 2 },
				},
				{
					label: 'You cannot shrink your way to rich. Earn more.',
					weights: { 'income-expansion': 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sm-risk',
			prompt: 'Your biggest money risk is probably:',
			options: [
				{
					label: 'My own behavior in a bad month.',
					weights: { 'behavioral-finance': 2 },
				},
				{
					label: 'Underearning for what my skills are worth.',
					weights: { 'income-expansion': 2 },
				},
				{
					label: 'Burning out before the finish line by sprinting too hard.',
					weights: { 'coast-fi-slow-fi': 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sm-superpower',
			prompt: 'Pick a superpower:',
			options: [
				{
					label: 'Perfect discipline on a boring plan, forever.',
					weights: { 'boglehead-indexing': 2 },
				},
				{
					label: 'Assets that pay me while I sleep.',
					weights: { 'cashflow-assets': 2 },
				},
				{
					label: 'Working two easy days a week, starting soon.',
					weights: { 'coast-fi-slow-fi': 2 },
				},
			],
		},
		{
			type: 'tradeoff',
			id: 'sm-friday',
			prompt: 'It is Friday night. The money move that would actually feel good:',
			options: [
				{
					label: 'Updating the spreadsheet and watching the date move closer.',
					weights: { 'fire-movement': 2 },
				},
				{
					label: 'Finding the same life for $200 less a month.',
					weights: { frugality: 2 },
				},
				{
					label: 'Journaling why I bought the thing I bought.',
					weights: { 'behavioral-finance': 2 },
				},
			],
		},
	],

	axes: {
		'boglehead-indexing': {
			label: 'The Bogleheads',
			insight:
				'Your instinct is to remove yourself from the equation: broad index funds, automatic contributions, and decades of leaving it alone. The evidence is heavily on your side, which is exactly why this school calls itself boring on purpose.',
			blindSpot:
				'Boring plans fail in exciting moments. The whole strategy rests on doing nothing during the one week everyone is doing something.',
			route: school('boglehead-indexing'),
			routeLabel: 'Explore the Bogleheads school in Nisk',
		},
		'fire-movement': {
			label: 'The FIRE Movement',
			insight:
				'You see work as optional and the date as the goal. High savings rate, hard math, early exit. The same savings-rate math from this school says half your income saved makes work optional in about 17 years.',
			blindSpot:
				'The sprint can eat the life it was supposed to buy. The years between now and the date still count as your life.',
			route: school('fire-movement'),
			routeLabel: 'Explore the FIRE school in Nisk',
		},
		'coast-fi-slow-fi': {
			label: 'Coast FI',
			insight:
				'You want the pressure off more than you want the exit early. Front-load the saving, then let growth carry it while you downshift to work you can live with. It is the patient cousin of FIRE.',
			blindSpot:
				'Coasting only works if the early block actually gets built. Downshifting before the math says so just moves the whole date.',
			route: school('coast-fi-slow-fi'),
			routeLabel: 'Explore the Coast FI school in Nisk',
		},
		'rich-life-ramit': {
			label: 'The Rich Life',
			insight:
				'You refuse the poverty-of-joy version of good finance. Spend lavishly on what you love, cut mercilessly on what you do not, and automate the rest. Values first, spreadsheet second.',
			blindSpot:
				'"What I love" expands under a generous rule. The system holds only when the mercilessly-cut half is genuinely merciless.',
			route: school('rich-life-ramit'),
			routeLabel: 'Explore the Rich Life school in Nisk',
		},
		'cashflow-assets': {
			label: 'Cashflow Assets',
			insight:
				'You trust income you can see: rent, dividends, businesses, royalties. A pile feels abstract. A payment hitting the account feels real. You build machines that pay you.',
			blindSpot:
				'Chasing visible income can quietly cost total growth, and the taxes on it arrive every single year instead of when you choose.',
			route: school('cashflow-assets'),
			routeLabel: 'Explore the Cashflow school in Nisk',
		},
		'behavioral-finance': {
			label: 'Behavioral Finance',
			insight:
				'You already know the market is not the hard part. You are. This school studies the gap between what people plan and what people do, and builds systems that survive the owner.',
			blindSpot:
				'Knowing the biases is not immunity from them. Insight without automation still loses to a bad Tuesday.',
			route: school('behavioral-finance'),
			routeLabel: 'Explore the Behavioral school in Nisk',
		},
		frugality: {
			label: 'Frugality',
			insight:
				'You see waste the way others see money on the sidewalk. Every recurring dollar you cut pays you again every month forever, and at a safe 4% yearly spend each $100 of monthly cost cut is $30,000 you never have to save.',
			blindSpot:
				'Optimization has a floor and the floor arrives fast. Past it, the hours spent saving pennies cost more than the pennies.',
			route: school('frugality'),
			routeLabel: 'Explore the Frugality school in Nisk',
		},
		'income-expansion': {
			label: 'Income Expansion',
			insight:
				'You play offense. Spending has a floor but earning has no ceiling, so the biggest lever is skills, leverage, and asking. This school treats your earning power as the portfolio.',
			blindSpot:
				'Income without a savings system just raises the water level. Plenty of high earners are one missed paycheck from trouble.',
			route: school('income-expansion'),
			routeLabel: 'Explore the Income school in Nisk',
		},
	},

	cta: {
		label: 'Browse all 39 schools',
		to: '/learn/schools',
		blurb: 'This matched you to one of eight big schools, but Nisk holds 39, from value investing to money psychology. Your school is a starting room, not a box.',
	},

	shareText:
		'Nisk matched me to {primary} out of 39 schools of money thinking. Eight questions, one honest blind spot: https://nisk.co/quizzes/school-match',
};
